import React, {useEffect, useMemo, useState} from 'react';
import {
  ProgressBar,
  TextField,
} from '@finpay-development/shared-components';
import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Button as MuiButton,
} from '@mui/material';
import HeaderWithDetails
  from '../../../../../shared/components/header-with-details';
import {formatNumberToUSD} from '../../../../../shared/calculators';
import {FormikProps} from 'formik';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import {useDispatch, useSelector} from 'react-redux';
import {AppDispatch} from '../../../../../shared/state/store';
import {RootState} from '../../../../../shared/state/root-reducer';
import {getEmployers} from '../../../../../meta-data/state/meta-data-thunk';
import {
  admissionsAdvisorService,
} from '../../../../../admissions-advisor/services/admissions-advisor-services';
import {showErrorStatus} from '../../../../../security/state/user-slice';
import AddressValidator
  from '../../../../../shared/components/address-validator';
import {ClientEmployerModal} from '../../client-employer-modal';

export const EmploymentInfoSection: React.FC<{
  formik: FormikProps<any>
  setCTPInitialValues: React.Dispatch<React.SetStateAction<{ zipCode: string, zipCode4: string, yearlySalary: number }>>
}> = ({formik, setCTPInitialValues}) => {

  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    setValues,
    touched,
    errors,
  } = formik;
  const dispatch = useDispatch<AppDispatch>();
  const clientEmployers = useSelector(
      (state: RootState) => {
        return state.metaData.employers;
      },
  );
  const employmentStatus = useSelector(
      (state: RootState) => {
        return state.metaData.employmentStatus;
      },
  );

  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const selectedEmploymentStatus = useMemo(() => {
    return employmentStatus.find(
        (status) => status.employmentStatusId === values.employmentStatusId);
  }, [values.employmentStatusId]);

  let isDisabled = !clientEmployers?.[values.clientId] ||
      clientEmployers?.[values.clientId].length === 0;

  useEffect(() => {

    const clientId = values.clientId;

    if (!clientId) return;
    const employers = clientEmployers[clientId];
    if (clientId && !employers) {
      dispatch(getEmployers(clientId));
    }
  }, [values.clientId, clientEmployers]);

  const handleCapacityToPayOnClick = async ({
                                              income,
                                              zipCode,
                                              zipCode4,
                                            }: {
    income: number,
    zipCode: string,
    zipCode4: string
  }) => {

    const response = await admissionsAdvisorService.getCapacityToPay(
        -2,
        income,
        zipCode,
        zipCode4,
    );

    if (response.hasErrors) {
      // we allow negatives for salary, and powerlytics doesn't
      // only returns what looks like a joi validation error
      if (response.entity ===
          'capacity to pay - BadRequest: "income" must be a positive number') {
        dispatch(
            showErrorStatus('Invalid income, please update and try again'));
        return;
      }

      if (response?.entity?.message) {
        dispatch(showErrorStatus(response.entity.message));
      } else {
        dispatch(showErrorStatus(
            'An error occurred. Please contact FinPay support.'));
      }

      return;
    }

    return response.entity.formatted

  };

  return (
      <>
        <Grid container spacing={2}>
          <Grid container item justifyContent="space-between" spacing={2}>
            <Grid item xs={6} xl={4.5} className="MuiTextField-root">
              <FormControl fullWidth size="small">
                <InputLabel id="employment-status-select">Employment
                  Status</InputLabel>
                <Select
                    labelId="employment-status-select"
                    label="Employment Status"
                    value={formik.values.employmentStatusId}
                    onChange={(e) => {
                      const selectedEmploymentStatusId = (e.target as HTMLInputElement).value;
                      formik.setFieldValue('employmentStatusId',
                          selectedEmploymentStatusId);
                    }}
                    style={{
                      width: '100%',
                    }}
                    renderValue={(selected) => {

                      return employmentStatus.find((status) => {
                        return status.employmentStatusId == selected;
                      })?.employmentStatus || 'Select Employment Status';

                    }}
                    test-id="patient-tab-employment-status-select"
                >
                  {employmentStatus.map((status) => (
                      <MenuItem key={status.employmentStatusId}
                                value={status.employmentStatusId}>
                        {status.employmentStatus}
                      </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid container item xs={6} xl={3} justifyContent="flex-end"
                  className="button">
              <MuiButton
                  disabled={isLoading}
                  onClick={async () => {
                    setIsLoading(true);
                    //Todo: Convert the following conditional to use Yup validation
                    if (!selectedEmploymentStatus?.enableCapacityToPay ||
                        !values.yearlySalary || !values.zipCode) {
                      let errorMsg = 'Please resolve the following issues: ';
                      if (!selectedEmploymentStatus?.enableCapacityToPay) errorMsg += "Capacity to Pay needs to be enabled for selected Employment Status; ";
                      if (!values.yearlySalary) errorMsg += 'Yearly Salary is required; ';
                      if(!values.zipCode) {
                        errorMsg += 'Zip Code is required; ';
                      }
                      if (!values.zipCode4) {
                        if (!values.streetAddress1) errorMsg += 'Street Address is required; ';
                        if (!values.city) errorMsg += 'City is required; ';
                        if (!values.state) errorMsg += 'State is required; ';
                      }
                      dispatch(showErrorStatus(errorMsg));
                      setIsLoading(false);
                      return;
                    }

                    let zipCode4 = values.zipCode4;
                    if (!values.zipCode4) {
                      const addressValidator = new AddressValidator();
                      let zipCode4Response = await addressValidator.getZipCodePlus4(
                          values);
                      if (['0', '1'].includes(zipCode4Response)) {
                        dispatch(showErrorStatus(
                            'Error retrieving Zip Code 4 value. To get Capacity to Pay, please manually input the Zip Code 4 value above or fill out Street Address, City , and State to get Zip Code 4 value needed to get Capacity to Pay'));
                        setIsLoading(false);
                        return;
                      }
                      zipCode4 = zipCode4Response;
                    }

                    const powerlyticsData = await handleCapacityToPayOnClick({
                      income: values.yearlySalary,
                      zipCode: values.zipCode,
                      zipCode4,
                    });

                    setValues({
                      ...values,
                      ...powerlyticsData,
                      zipCode4,
                    });

                    if (powerlyticsData) {
                      //reinitialize capacity to pay initial values if Capacity to Pay call succeeds
                      setCTPInitialValues({
                        yearlySalary: values.yearlySalary,
                        zipCode: values.zipCode,
                        zipCode4,
                      });
                    }
                    setIsLoading(false);

                  }}
                  test-id="patient-tab-capacity-to-pay"
                  fullWidth
              >
                Run Capacity to Pay
              </MuiButton>
            </Grid>
          </Grid>
          <Grid
              container
              item
              xs={12}
              md={6}
              spacing={2}
              className="MuiTextField-root"
          >
            <Grid
                item
                sm={12}
                md={9}
            >
              <FormControl fullWidth size="small">
                <InputLabel id="employer-select">Employer</InputLabel>
                <Select
                    labelId="employer-select"
                    test-id="patient-tab-employer-select"
                    label="Employer"
                    value={values.clientEmployerId}
                    disabled={isDisabled}
                    onChange={(e) => {
                      let employerValue = (e.target as HTMLInputElement).value;
                      if (employerValue) {
                        setFieldValue('clientEmployerId', employerValue);
                      }
                    }}
                    style={{
                      width: '100%',
                    }}
                    renderValue={(selected) => {
                      const selectedEmployer = clientEmployers[values.clientId]?.
                          find((employer) => {
                            return employer.clientEmployerId === selected;
                          });
                      return selectedEmployer?.employerName ||
                          'Select Employer';

                    }}
                    className={`${touched.clientEmployerId &&
                    Boolean(errors.clientEmployerId) ? 'error-border' : ''}`}
                >
                  {clientEmployers[values.clientId] && (
                      clientEmployers[values.clientId]?.
                          map((employer) => {
                            return (
                                <MenuItem
                                    key={employer.clientEmployerId}
                                    value={employer.clientEmployerId}
                                    test-id={`patient-tab-employer-select-menu-${employer.clientEmployerId}`}
                                >
                                  {employer.employerName}
                                </MenuItem>
                            );
                          })
                  )}
                </Select>
              </FormControl>
            </Grid>
            <Grid
                item
                sm={12}
                md={3}
            >
              <MuiButton
                  onClick={() => setIsModalOpen(true)}
                  test-id="patient-tab-new-employer-btn"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disableRipple
                  sx={{
                    fontWeight: 'bold',
                    textTransform: 'none',
                    minWidth: '0',
                  }}
              >
                New
              </MuiButton>

            </Grid>
          </Grid>
          <Grid item xs={3}>
            <TextField
                name="yearlySalary"
                label="Yearly Salary"
                type="number"
                value={values.yearlySalary || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter Yearly Salary"
                startAdornment={<AttachMoneyIcon/>}
                className={`${touched.yearlySalary &&
                Boolean(errors.yearlySalary) ? 'error-border' : ''}`}
                test-id="patient-tab-yearly-salary"
            />
          </Grid>
          <Grid item xs={3}>
            <TextField
                name="yearsAtEmployer"
                label="Years at Employer"
                type="number"
                value={values.yearsAtEmployer || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Enter Years At Employer"
                className={`${touched.yearsAtEmployer &&
                Boolean(errors.yearsAtEmployer) ? 'error-border' : ''}`}
            />
          </Grid>
          <Grid item xs={6}>
            <ProgressBar
                headerText="Verification score"
                completeness={selectedEmploymentStatus?.enableCapacityToPay ?
                    values.verificationScore || 0 : 0}
            />
          </Grid>
          <Grid item xs={6}>
            <ProgressBar
                headerText="Capacity to Pay"
                completeness={selectedEmploymentStatus?.enableCapacityToPay ?
                    values.capacityToPay || 0 : 0}
            />
          </Grid>
          <HeaderWithDetails
              header={'Disposable Income'}
              details={`${formatNumberToUSD(
                  selectedEmploymentStatus?.enableCapacityToPay ?
                      values.disposableIncome ||
                      0 : 0)}`}
              width="half"
              testId="disposable-income-value"
          />
          <HeaderWithDetails
              header={'Retirement Income'}
              details={`${formatNumberToUSD(
                  selectedEmploymentStatus?.enableCapacityToPay ?
                      values.retirementIncome ||
                      0 : 0)}`}
              width="half"
              testId="retirement-income-value"
          />
          <HeaderWithDetails
              header={'Business Income'}
              details={`${formatNumberToUSD(
                  selectedEmploymentStatus?.enableCapacityToPay ?
                      values.businessIncome ||
                      0 : 0)}`}
              width="half"
              testId="business-income-value"
          />
          <HeaderWithDetails
              header={'Wealth Income'}
              details={`${formatNumberToUSD(
                  selectedEmploymentStatus?.enableCapacityToPay ?
                      values.wealthIncome || 0 :
                      0)}`}
              width="half"
              testId="wealth-income-value"
          />
          <HeaderWithDetails
              header={'Wages and Earnings'}
              details={`${formatNumberToUSD(
                  selectedEmploymentStatus?.enableCapacityToPay ?
                      values.wagesAndEarnings ||
                      0 : 0)}`}
              width="full"
              testId="wages-earnings-value"
          />
        </Grid>
        <ClientEmployerModal modalOpenState={{isModalOpen, setIsModalOpen}}
                             selectedClientId={values.clientId}/>
      </>
  );
};